import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { useAuth, hasMinRole } from "@/context/AuthContext";
import { Loading, EmptyState, PageHeader } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Eye, Plus, Trash2, Pencil, Bell, X } from "lucide-react";

const inputCls = "w-full h-10 rounded-lg bg-white border border-brand-line px-3 text-sm text-brand-ink outline-none focus:border-brand-civic";
const primaryBtn = "inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navy-700 transition-colors";
const outlineBtn = "inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-brand-line bg-white text-sm text-brand-navy font-medium hover:border-brand-civic/50 hover:bg-brand-civic-50 transition-colors";

const DELIVERY_LABEL = { immediate: "Immediate", hourly: "Hourly digest", daily: "Daily digest" };

export default function Watchlists() {
  const { user } = useAuth();
  const isManager = hasMinRole(user?.role, "manager");
  return (
    <div>
      <PageHeader title="Watchlists" subtitle="Define what legislative activity you want to monitor. Matches surface as notifications and appear below." />
      <Tabs defaultValue="lists">
        <TabsList className="bg-white border border-brand-line" data-testid="watchlists-tabs">
          <TabsTrigger value="lists" data-testid="tab-lists" className="data-[state=active]:bg-brand-navy data-[state=active]:text-white text-slate-500">My Watchlists</TabsTrigger>
          <TabsTrigger value="matches" data-testid="tab-matches" className="data-[state=active]:bg-brand-navy data-[state=active]:text-white text-slate-500">Recent Matches</TabsTrigger>
        </TabsList>
        <TabsContent value="lists" className="mt-4"><Lists isManager={isManager} /></TabsContent>
        <TabsContent value="matches" className="mt-4"><Matches /></TabsContent>
      </Tabs>
    </div>
  );
}

function Lists({ isManager }) {
  const [data, setData] = useState(null);
  const [opts, setOpts] = useState({ criteria_types: [], delivery: [], event_types: [] });
  const [showAll, setShowAll] = useState(false);
  const load = () => { setData(null); api.get(`/watchlists${showAll ? "?all=true" : ""}`).then((r) => { setData(r.data.watchlists); setOpts({ criteria_types: r.data.criteria_types, delivery: r.data.delivery, event_types: r.data.event_types }); }).catch(() => setData(false)); };
  useEffect(load, [showAll]);

  const remove = async (id) => { if (!window.confirm("Delete this watchlist? This cannot be undone.")) return; try { await api.delete(`/watchlists/${id}`); toast.success("Watchlist deleted"); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };
  const toggle = async (wl) => { try { await api.put(`/watchlists/${wl.watchlist_id}`, { name: wl.name, criteria: wl.criteria, active: !wl.active, delivery: wl.delivery }); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        {isManager && (
          <label className="flex items-center gap-2 text-sm text-slate-600" data-testid="watchlist-show-all">
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="rounded border-brand-line" /> Show all team watchlists
          </label>
        )}
        <div className="ml-auto"><WatchlistDialog opts={opts} onDone={load} /></div>
      </div>
      {data === null ? <Loading /> : !data || data.length === 0 ? <EmptyState icon={Eye} title="No watchlists yet" description="Create a watchlist to monitor bills, committees, sponsors, topics, or event types." /> : (
        <div className="grid gap-3 md:grid-cols-2" data-testid="watchlists-list">
          {data.map((wl) => (
            <div key={wl.watchlist_id} className="rounded-xl border border-brand-line bg-white p-4 shadow-card" data-testid={`watchlist-${wl.watchlist_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-semibold text-brand-ink">{wl.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase ${wl.active ? "bg-brand-teal-50 text-brand-teal" : "bg-slate-100 text-slate-500"}`}>{wl.active ? "Active" : "Inactive"}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">{DELIVERY_LABEL[wl.delivery] || wl.delivery} · {wl.match_count || 0} match(es){wl.last_match_at ? ` · last ${fmtDate(wl.last_match_at)}` : ""}{wl.owner?.name ? ` · ${wl.owner.name}` : ""}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <WatchlistDialog opts={opts} existing={wl} onDone={load} />
                  <button onClick={() => toggle(wl)} data-testid={`watchlist-toggle-${wl.watchlist_id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-brand-navy hover:bg-slate-50" title={wl.active ? "Deactivate" : "Activate"}><Bell className="h-4 w-4" /></button>
                  <button onClick={() => remove(wl.watchlist_id)} data-testid={`watchlist-delete-${wl.watchlist_id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(wl.criteria || []).map((c, i) => (
                  <span key={i} className="text-xs px-2 py-1 rounded-lg bg-brand-civic-50 text-brand-civic border border-brand-civic/20"><span className="font-semibold capitalize">{c.type.replace(/_/g, " ")}:</span> {c.value}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WatchlistDialog({ opts, existing, onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(existing?.name || "");
  const [delivery, setDelivery] = useState(existing?.delivery || "immediate");
  const [active, setActive] = useState(existing?.active ?? true);
  const [criteria, setCriteria] = useState(existing?.criteria?.length ? existing.criteria : [{ type: "keyword", value: "" }]);

  const reset = () => { setName(existing?.name || ""); setDelivery(existing?.delivery || "immediate"); setActive(existing?.active ?? true); setCriteria(existing?.criteria?.length ? existing.criteria : [{ type: "keyword", value: "" }]); };
  const addCrit = () => setCriteria([...criteria, { type: "keyword", value: "" }]);
  const setCrit = (i, patch) => setCriteria(criteria.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const rmCrit = (i) => setCriteria(criteria.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!name.trim()) return toast.error("Enter a name");
    const clean = criteria.filter((c) => c.value.trim());
    if (!clean.length) return toast.error("Add at least one criterion with a value");
    const payload = { name: name.trim(), delivery, active, criteria: clean };
    try {
      if (existing) await api.put(`/watchlists/${existing.watchlist_id}`, payload);
      else await api.post("/watchlists", payload);
      toast.success(existing ? "Watchlist updated" : "Watchlist created");
      setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) reset(); }}>
      <DialogTrigger asChild>
        {existing
          ? <button data-testid={`watchlist-edit-${existing.watchlist_id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-brand-navy hover:bg-slate-50"><Pencil className="h-4 w-4" /></button>
          : <button data-testid="new-watchlist-button" className={primaryBtn}><Plus className="h-4 w-4" /> New Watchlist</button>}
      </DialogTrigger>
      <DialogContent className="bg-white border-brand-line text-brand-ink max-w-lg">
        <DialogHeader><DialogTitle className="text-brand-ink">{existing ? "Edit watchlist" : "Create watchlist"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input data-testid="watchlist-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Watchlist name (e.g., Insurance Legislation)" className={inputCls} />
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="watchlist-delivery" value={delivery} onChange={(e) => setDelivery(e.target.value)} className={inputCls}>
              {(opts.delivery || []).map((d) => <option key={d} value={d}>{DELIVERY_LABEL[d] || d}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-600 px-1"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="watchlist-active" /> Active</label>
          </div>
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Criteria (any match triggers)</div>
            {criteria.map((c, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`criterion-row-${i}`}>
                <select value={c.type} onChange={(e) => setCrit(i, { type: e.target.value })} data-testid={`criterion-type-${i}`} className="h-9 rounded-lg bg-white border border-brand-line px-2 text-sm text-brand-ink outline-none focus:border-brand-civic w-40">
                  {(opts.criteria_types || []).map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </select>
                {c.type === "event_type"
                  ? <select value={c.value} onChange={(e) => setCrit(i, { value: e.target.value })} data-testid={`criterion-value-${i}`} className="flex-1 h-9 rounded-lg bg-white border border-brand-line px-2 text-sm text-brand-ink outline-none focus:border-brand-civic"><option value="">Select event…</option>{(opts.event_types || []).map((ev) => <option key={ev} value={ev}>{ev.replace(/_/g, " ")}</option>)}</select>
                  : <input value={c.value} onChange={(e) => setCrit(i, { value: e.target.value })} data-testid={`criterion-value-${i}`} placeholder="Value…" className="flex-1 h-9 rounded-lg bg-white border border-brand-line px-3 text-sm text-brand-ink outline-none focus:border-brand-civic" />}
                {criteria.length > 1 && <button onClick={() => rmCrit(i)} className="p-1.5 text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>}
              </div>
            ))}
            <button onClick={addCrit} data-testid="add-criterion" className={outlineBtn}><Plus className="h-3.5 w-3.5" /> Add criterion</button>
          </div>
        </div>
        <DialogFooter><button onClick={submit} data-testid="watchlist-submit" className="h-10 px-4 rounded-lg bg-brand-navy text-white font-semibold hover:bg-brand-navy-700">{existing ? "Save" : "Create"}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Matches() {
  const [matches, setMatches] = useState(null);
  useEffect(() => { api.get("/watchlists/matches").then((r) => setMatches(r.data.matches)).catch(() => setMatches(false)); }, []);
  if (matches === null) return <Loading />;
  if (!matches || matches.length === 0) return <EmptyState icon={Bell} title="No matches yet" description="When new legislative activity matches your watchlists, it appears here." />;
  return (
    <div className="rounded-xl border border-brand-line bg-white divide-y divide-brand-line shadow-card" data-testid="watchlist-matches">
      {matches.map((m) => (
        <div key={m.match_id} className="flex items-start justify-between gap-3 p-4" data-testid={`match-${m.match_id}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 uppercase font-bold">{(m.event_type || "").replace(/_/g, " ")}</span>
              <span className="text-xs text-brand-civic font-medium">{m.watchlist_name}</span>
              <span className="text-xs text-slate-400">{fmtDate(m.created_at)}</span>
            </div>
            <p className="text-sm text-slate-700 mt-1">{m.description}</p>
          </div>
          {m.bill_id && <Link to={`/bills/${m.bill_id}`} className={outlineBtn}>{m.bill?.bill_number || "View bill"}</Link>}
        </div>
      ))}
    </div>
  );
}
